import { useObjectUrl, useObjectUrls } from '../../hooks/useObjectUrl';
import type { ImageDimensions } from '../../utils/imageDimensions';
import { getMediaType } from '../../utils/mediaUtils';
import { S3Image } from '../S3Image';
import { DappPreviewShell } from '../shared/DappPreviewShell';

interface FormData {
  dappName: string;
  subtitle: string;
  description: string;
  dappIcon: File | null;
  banner: File | null;
  dappPreviews: (File | null)[];
  existingDappIconUrl?: string | null;
  existingBannerUrl?: string | null;
  existingPreviewUrls?: (string | null)[];
}

interface DappPreviewPanelWithUrlsProps {
  formData: FormData;
  maxPreviews: number;
  previewDimensions?: ImageDimensions | null;
}

export function DappPreviewPanelWithUrls({
  formData,
  maxPreviews,
  previewDimensions,
}: DappPreviewPanelWithUrlsProps) {
  const dappPreviewUrls = useObjectUrls(formData.dappPreviews);
  const iconPreviewUrl = useObjectUrl(formData.dappIcon);
  const bannerPreviewUrl = useObjectUrl(formData.banner);

  const displayIconUrl = iconPreviewUrl || formData.existingDappIconUrl;
  const displayBannerUrl = bannerPreviewUrl || formData.existingBannerUrl;
  const displayPreviewUrls = Array(maxPreviews)
    .fill(null)
    .map(
      (_, index) =>
        dappPreviewUrls[index] || formData.existingPreviewUrls?.[index] || null,
    );

  const previewSlots = displayPreviewUrls.map((previewUrl, index) => {
    const filePreview = formData.dappPreviews[index];
    const slotKey = `preview-slot-${index}`;

    if (!previewUrl) {
      return null;
    }

    const mediaType = getMediaType(filePreview || previewUrl);

    if (mediaType === 'video') {
      return filePreview ? (
        <video
          key={slotKey}
          src={previewUrl}
          aria-label={`Preview ${index + 1} video`}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-contain rounded-2xl"
        />
      ) : (
        <video
          key={slotKey}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-contain rounded-2xl"
        >
          <source src={previewUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    return filePreview ? (
      <img
        key={slotKey}
        src={previewUrl}
        alt={`Preview ${index + 1}`}
        className="w-full h-full object-cover rounded-2xl"
      />
    ) : (
      <S3Image
        key={slotKey}
        src={previewUrl}
        alt={`Preview ${index + 1}`}
        className="w-full h-full object-cover rounded-2xl"
      />
    );
  });

  return (
    <DappPreviewShell
      dappName={formData.dappName}
      subtitle={formData.subtitle}
      description={formData.description}
      bannerSlot={
        displayBannerUrl ? (
          formData.banner ? (
            <img
              src={displayBannerUrl}
              alt="Banner Preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <S3Image
              src={displayBannerUrl}
              alt="Banner Preview"
              className="w-full h-full object-cover"
            />
          )
        ) : null
      }
      iconSlot={
        displayIconUrl ? (
          formData.dappIcon ? (
            <img
              src={displayIconUrl}
              alt="Icon Preview"
              className="w-full h-full object-cover rounded-3xl"
            />
          ) : (
            <S3Image
              src={displayIconUrl}
              alt="Icon Preview"
              className="w-full h-full object-cover rounded-3xl"
            />
          )
        ) : null
      }
      previewSlots={previewSlots}
      previewDimensions={previewDimensions}
    />
  );
}
